import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useEffect, useState } from 'react';
import { TicketPercent } from 'lucide-react';
import { fetchCuponesUsados, CuponUsado } from '../../../services/usedCouponService';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import Pagination from '../../../components/Pagination/Pagination';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';


const ITEMS_PER_PAGE = 10;

const UsedCouponPage: React.FC = () => {
    const [cuponesUsados, setCuponesUsados] = useState<CuponUsado[]>([]);
    const [currentPage, setCurrentPage] = useState(1);

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout } = useAuth();
    const navigate = useNavigate();


    const cargarDatos = async () => {
        try {
            const datos = await fetchCuponesUsados();
            // Ordenar por fecha de uso descendente
            const ordenados = datos.sort((a, b) => new Date(b.fecha_uso).getTime() - new Date(a.fecha_uso).getTime());
            setCuponesUsados(ordenados);
        } catch (error) {
            /* Browser diagnostics omitted. */
            setCuponesUsados([]);
        }
    };

    useEffect(() => {
        cargarDatos();
    }, []);

    // Paginación
    const totalPages = Math.ceil(cuponesUsados.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const cuponesMostrados = cuponesUsados.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Cupones Usados</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/coupon-used" className="text-[#c1348b] hover:text-[#8f1f64]">Cupones Usados</a>
                    </div>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Modal de Sesión Expirada */}
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cita</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cupón Usado</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha de Uso</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha de Registro</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {cuponesMostrados.map((cupon) => (
                                <tr
                                    key={cupon.id}
                                    className="hover:bg-gray-100 cursor-pointer"
                                    onClick={() => navigate(`/cita-coupon-detalle/${cupon.cita_id}`)}
                                >
                                    <td className="px-4 py-4 text-sm text-gray-900">{cupon.cita_id ?? '—'}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{cupon.cliente_nombre}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">{cupon.cupon_codigo}</td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        {new Date(cupon.fecha_uso).toLocaleString()}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        {cupon.fecha_creacion ? new Date(cupon.fecha_creacion).toLocaleString() : '—'}
                                    </td>
                                </tr>
                            ))}
                            {cuponesUsados.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <TicketPercent className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">No hay registros de cupones usados</span>
                                            <p className="text-gray-500 text-sm">Cuando se usen cupones, aparecerán aquí.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {/* Paginación */}
                {cuponesUsados.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={ITEMS_PER_PAGE}
                        totalItems={cuponesUsados.length}
                    />
                )}
            </div>
        </div>
    );
};

export default UsedCouponPage;

